import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Flex, Text, VStack, Input, InputGroup, InputLeftElement, Select, Icon, Skeleton, Alert, AlertIcon,
    AlertDescription, Button, SimpleGrid,
} from '@chakra-ui/react';
import { FaSearch, FaInbox } from 'react-icons/fa';
import CartaoFechamento from './CartaoFechamento';
import RodapePaginacao from './RodapePaginacao';
import { getFechamentosComissao } from '../../../services/api';
import { mensagemErro } from '../formato';

const POR_PAGINA = 10;
const INICIAL = { situacao: 'todos', de: '', ate: '', busca: '', ordem: 'recentes' };

// Pagamentos registrados, com filtros e paginacao feitos na API (lista que cresce).
// "versao" muda quando outra parte da tela registra ou desfaz algo: recarrega.
const ListaFechamentos = ({ userId, versao, podeGerenciar, onDesfazer, onDesfazerItem, onReenviar }) => {
    const [filtro, setFiltro] = useState(INICIAL);
    const [busca, setBusca] = useState('');
    const [pagina, setPagina] = useState(1);
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);

    // Digitar na busca so consulta depois de uma pausa; filtro novo volta para a pagina 1.
    useEffect(() => {
        const b = busca.trim();
        if (b === filtro.busca) return undefined;
        const t = setTimeout(() => { setFiltro((f) => ({ ...f, busca: b })); setPagina(1); }, 400);
        return () => clearTimeout(t);
    }, [busca, filtro.busca]);

    const carregar = useCallback(async () => {
        if (!userId) return;
        setCarregando(true);
        setErro(null);
        try {
            const params = { pagina, por_pagina: POR_PAGINA, situacao: filtro.situacao, ordem: filtro.ordem };
            if (filtro.de) params.de = filtro.de;
            if (filtro.ate) params.ate = filtro.ate;
            if (filtro.busca) params.busca = filtro.busca;
            setDados(await getFechamentosComissao(userId, params));
        } catch (e) {
            setErro(mensagemErro(e, 'Não foi possível carregar os pagamentos.'));
        } finally {
            setCarregando(false);
        }
    }, [userId, pagina, filtro]);

    useEffect(() => { carregar(); }, [carregar, versao]);

    const set = (campo) => (e) => { setFiltro((f) => ({ ...f, [campo]: e.target.value })); setPagina(1); };
    const filtrando = filtro.situacao !== 'todos' || filtro.de || filtro.ate || filtro.busca;

    return (
        <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" boxShadow="sm" overflow="hidden" mb={5}>
            <Box px={{ base: 3, md: 4 }} py={3} bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.100">
                <SimpleGrid columns={{ base: 2, md: 6 }} spacing={2}>
                    <InputGroup size="sm" gridColumn={{ base: '1 / -1', md: 'span 2' }}>
                        <InputLeftElement pointerEvents="none"><Icon as={FaSearch} color="gray.400" /></InputLeftElement>
                        <Input bg="white" borderRadius="md" placeholder="N.º do pagamento, venda ou pedido" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar pagamentos" />
                    </InputGroup>
                    <Select size="sm" bg="white" borderRadius="md" value={filtro.situacao} onChange={set('situacao')} aria-label="Situação do pagamento">
                        <option value="todos">Todos</option>
                        <option value="ativos">Ativos</option>
                        <option value="desfeitos">Desfeitos</option>
                        <option value="pendencia_site">Com pendência no site</option>
                    </Select>
                    <Select size="sm" bg="white" borderRadius="md" value={filtro.ordem} onChange={set('ordem')} aria-label="Ordenar pagamentos">
                        <option value="recentes">Mais recentes</option>
                        <option value="antigos">Mais antigos</option>
                        <option value="maior_valor">Maior valor</option>
                        <option value="menor_valor">Menor valor</option>
                    </Select>
                    <Flex gap={2} gridColumn={{ base: '1 / -1', md: 'span 2' }}>
                        <Input size="sm" bg="white" borderRadius="md" type="date" value={filtro.de} onChange={set('de')} aria-label="Pagos a partir de" title="Pagos a partir de" />
                        <Input size="sm" bg="white" borderRadius="md" type="date" value={filtro.ate} onChange={set('ate')} aria-label="Pagos até" title="Pagos até" />
                    </Flex>
                </SimpleGrid>
            </Box>

            {erro ? (
                <Alert status="error" m={4} w="auto" borderRadius="md" fontSize="sm" flexWrap="wrap" gap={2}>
                    <AlertIcon />
                    <AlertDescription flex="1">{erro}</AlertDescription>
                    <Button size="xs" onClick={carregar}>Tentar de novo</Button>
                </Alert>
            ) : !dados ? (
                <VStack p={4} spacing={3}>{[0, 1].map((i) => <Skeleton key={i} h="64px" w="100%" borderRadius="lg" />)}</VStack>
            ) : dados.fechamentos.length === 0 ? (
                <Flex direction="column" align="center" py={8} color="gray.500">
                    <Icon as={FaInbox} boxSize={8} color="gray.300" mb={2} />
                    <Text fontSize="sm">{filtrando ? 'Nenhum pagamento neste filtro.' : 'Nenhum pagamento registrado ainda.'}</Text>
                    {filtrando && (
                        <Button size="xs" variant="link" colorScheme="orange" mt={1} onClick={() => { setBusca(''); setFiltro(INICIAL); setPagina(1); }}>Limpar filtros</Button>
                    )}
                </Flex>
            ) : (
                <VStack align="stretch" spacing={3} p={{ base: 2, md: 3 }} opacity={carregando ? 0.6 : 1} transition="opacity 0.15s" aria-busy={carregando}>
                    {dados.fechamentos.map((f) => (
                        <CartaoFechamento
                            key={f.id}
                            fechamento={f}
                            podeGerenciar={podeGerenciar}
                            onDesfazer={onDesfazer}
                            onDesfazerItem={onDesfazerItem}
                            onReenviar={onReenviar}
                        />
                    ))}
                </VStack>
            )}
            {dados && (
                <RodapePaginacao
                    pagina={dados.paginacao.pagina}
                    paginas={dados.paginacao.paginas}
                    total={dados.paginacao.total}
                    porPagina={dados.paginacao.por_pagina}
                    onMudar={setPagina}
                    rotulo="pagamentos"
                />
            )}
        </Box>
    );
};

export default ListaFechamentos;
